import { useState, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Image from 'components/Common/Image'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextLink from 'LuxKit/TextLink'
import Divider from 'LuxKit/Divider'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import SolidShieldCheckIcon from 'LuxKit/Icons/solid/SolidShieldCheckIcon'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import FlashOfferSignUpPrice from './FlashOfferSignUpPrice'

const Card = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  overflow: hidden;
  min-width: 0;
`

const Hero = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    position: relative;
    display: block;
    aspect-ratio: 406 / 174;
    overflow: hidden;

    picture {
      position: absolute;
      inset: 0;
    }

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`

const Content = styled.div`
  display: grid;
  gap: ${rem(12)};
  padding: ${rem(16)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(16)};
    padding: ${rem(20)};
  }
`

const Summary = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${rem(108.4)};
  align-items: start;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    display: block;
  }
`

const MobileImage = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 5 / 4;
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.M};
  align-self: start;

  picture {
    position: absolute;
    inset: 0;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const Intro = styled.div`
  display: grid;
  gap: ${rem(8)};
`

const IntroCopy = styled.div`
  display: grid;
  gap: ${rem(4)};
`

const OfferName = styled(Heading)`
  overflow-wrap: anywhere;
`

const InclusionsSection = styled.div`
  display: grid;
  gap: ${rem(8)};
`

const InclusionList = styled.div`
  display: grid;
  gap: ${rem(4)};
`

const Inclusions = styled.div`
  display: grid;
  gap: ${rem(4)};

  > .extra-mobile {
    display: none;

    ${mediaQueryUp.tablet} {
      display: block;
    }
  }

  > [hidden] {
    display: none;
  }
`

const MobileMore = styled.div`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const DesktopMore = styled.div`
  display: none;
  ${mediaQueryUp.tablet} {
    display: block;
  }
`

const DesktopPrice = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: grid;
    gap: ${rem(16)};
  }
`

const MobilePrice = styled.div`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

interface Props {
  offer: App.HotelOffer
  pkg: App.HotelPackage
  price: number
  memberPrice?: number
  discountPercent?: number
  checkInDate?: string
}

function FlashOfferSignUpDetails({ offer, pkg, price, memberPrice, discountPercent, checkInDate }: Props) {
  const intl = useIntl()
  const cancellationIconColour = useRebrandSuccessDarkColour()
  const [showAllInclusions, setShowAllInclusions] = useState(false)
  const toggleInclusions = useCallback(() => setShowAllInclusions(show => !show), [])
  const inclusions = pkg.inclusions
  const mobilePreviewCount = 2
  const desktopPreviewCount = 5
  const image = offer.images[0] ?? offer.image
  const priceCaption = intl.formatMessage({
    id: 'ZJLsMgMjJuEbLzPsm-rc',
    defaultMessage: '{nights, plural, one {# night from} other {# nights from}}',
    description: 'Caption before the live package price in the flash offer sign-up modal',
  }, { nights: pkg.duration })
  const displayedInclusions = useMemo(() => (inclusions ?? []).map(inclusion => (
    inclusion.symbol ? inclusion : {
      ...inclusion,
      Icon: SolidCheckCircleIcon,
      iconColour: 'success-dark' as const,
    }
  )), [inclusions])
  const inclusionCount = inclusions?.length ?? 0
  const mobileMoreCount = inclusionCount - mobilePreviewCount
  const desktopMoreCount = inclusionCount - desktopPreviewCount
  return <Card data-testid="flash-offer-sign-up-details">
    {image && <Hero><Image image={image} fit="center" alt={offer.property.name} /></Hero>}
    <Content>
      <Summary>
        <Intro>
          <IntroCopy>
            {offer.locationHeading && <CSSBreakpoint min="tablet">
              <BodyText variant="small">{offer.locationHeading}</BodyText>
            </CSSBreakpoint>}
            <OfferName variant="heading3">{offer.property.name}</OfferName>
          </IntroCopy>
          {isOfferRatingDisplayable(offer.property.rating) && <CSSBreakpoint min="tablet">
            <OfferRating
              variant="small"
              inlineLabel
              link
              rating={offer.property.rating}
            />
          </CSSBreakpoint>}
          <MobilePrice data-testid="flash-sign-up-mobile-price">
            {price > 0 && <FlashOfferSignUpPrice
              caption={priceCaption}
              price={price}
              memberPrice={memberPrice}
              saleUnit={offer.saleUnit}
              offerConfig={offer.luxPlus}
              discountPercent={discountPercent}
            />}
          </MobilePrice>
        </Intro>
        {image && <MobileImage><Image image={image} fit="center" alt="" /></MobileImage>}
      </Summary>
      {(inclusionCount > 0 || pkg.roomRate?.cancellationPolicy) && <InclusionsSection>
        <InclusionList>
          {pkg.roomRate?.cancellationPolicy && <>
            <CSSBreakpoint max="mobile">
              <FlashCancellationPolicies
                cancellationPolicy={pkg.roomRate.cancellationPolicy}
                checkInDate={checkInDate}
                charterDepartDate={undefined}
                propertyTimezoneOffset={offer.property?.timezoneOffset}
                numberOfDateChangesAllowed={offer.numberOfDateChangesAllowed}
                leadingIcon={<SolidShieldCheckIcon size="XS" colour={cancellationIconColour} />}
                leadingIconGap={4}
                hideNonRefundable
                textVariant="small"
              />
            </CSSBreakpoint>
            <CSSBreakpoint min="tablet">
              <FlashCancellationPolicies
                cancellationPolicy={pkg.roomRate.cancellationPolicy}
                checkInDate={checkInDate}
                charterDepartDate={undefined}
                propertyTimezoneOffset={offer.property?.timezoneOffset}
                numberOfDateChangesAllowed={offer.numberOfDateChangesAllowed}
                leadingIcon={<SolidShieldCheckIcon size="XS" colour={cancellationIconColour} />}
                hideNonRefundable
              />
            </CSSBreakpoint>
          </>}
          {inclusionCount > 0 && <CSSBreakpoint min="tablet">
            <BodyText variant="small" weight="bold">
              {intl.formatMessage({
                id: 'uy_XEL_utkT2P1v36uYN',
                defaultMessage: 'Included in your stay',
                description: 'Heading above package inclusions in the flash offer sign-up modal',
              })}
            </BodyText>
          </CSSBreakpoint>}
          {inclusionCount > 0 && <Inclusions>
            {displayedInclusions.map((inclusion, index) => <div key={inclusion.id} className={!showAllInclusions && index >= mobilePreviewCount ? 'extra-mobile' : undefined} hidden={!showAllInclusions && index >= desktopPreviewCount}>
              <OfferInclusionItem inclusion={inclusion} size="small" lineClamp={1} iconSize="XS" />
            </div>)}
          </Inclusions>}
        </InclusionList>
        {inclusionCount > 0 && <>
          {showAllInclusions && <TextLink size="small" weight="bold" underline={false} onClick={toggleInclusions}>
            {intl.formatMessage({
              id: 'FNKxHD3fFrTa43MyF1At',
              defaultMessage: 'View less inclusions',
              description: 'Toggle text to collapse inclusions list on accommodation offer page',
            })}
          </TextLink>}
          {!showAllInclusions && mobileMoreCount > 0 && <MobileMore>
            <TextLink size="small" weight="bold" underline={false} onClick={toggleInclusions}>
              {intl.formatMessage({
                id: '1G_QMwYEj6TGWAVkXNqh',
                defaultMessage: '+{count} more inclusions',
                description: 'Button revealing additional package inclusions in the flash offer sign-up modal',
              }, { count: mobileMoreCount })}
            </TextLink>
          </MobileMore>}
          {!showAllInclusions && desktopMoreCount > 0 && <DesktopMore>
            <TextLink size="small" weight="bold" underline={false} onClick={toggleInclusions}>
              {intl.formatMessage({
                id: '1G_QMwYEj6TGWAVkXNqh',
                defaultMessage: '+{count} more inclusions',
                description: 'Button revealing additional package inclusions in the flash offer sign-up modal',
              }, { count: desktopMoreCount })}
            </TextLink>
          </DesktopMore>}
        </>}
      </InclusionsSection>}
      <DesktopPrice data-testid="flash-sign-up-desktop-price">
        <Divider kind="primary" />
        {price > 0 && <FlashOfferSignUpPrice
          caption={priceCaption}
          price={price}
          memberPrice={memberPrice}
          saleUnit={offer.saleUnit}
          offerConfig={offer.luxPlus}
          discountPercent={discountPercent}
        />}
      </DesktopPrice>
    </Content>
  </Card>
}

export default FlashOfferSignUpDetails
